import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, PieChart, Activity, Calendar } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortfolioSummary = ({ selectedFund, selectedStage, investments, onFmvDateChange, onCurrencyChange }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fmvDate, setFmvDate] = useState('');
  const [tempFmvDate, setTempFmvDate] = useState('');
  const [currencyFormat, setCurrencyFormat] = useState('full'); // 'full', 'crores', 'millions'
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState(83.5); // Default INR to USD rate

  useEffect(() => {
    console.log('PortfolioSummary useEffect triggered:', {
      investmentsCount: investments?.length,
      selectedFund,
      selectedStage,
      fmvDate
    });
    calculateSummary();
  }, [investments, fmvDate, selectedFund, selectedStage, selectedCurrency, exchangeRate]);

  const convertToDisplayCurrency = (amount, sourceCurrency) => {
    if (amount == null) return 0;
    const src = (sourceCurrency || 'INR').toUpperCase();
    const tgt = selectedCurrency.toUpperCase();
    if (src === tgt) return amount;
    if (src === 'INR' && tgt === 'USD') return amount / exchangeRate;
    if (src === 'USD' && tgt === 'INR') return amount * exchangeRate;
    return amount;
  };

  const calculateSummary = () => {
    if (!investments || investments.length === 0) {
      console.log('No investments to calculate summary');
      setSummary({
        total_count: 0,
        total_investment: 0,
        total_realisation: 0,
        total_fmv: 0,
        moic: 0,
        portfolio_irr: 0
      });
      return;
    }

    console.log('Calculating summary for', investments.length, 'investments');

    // Calculate summary from filtered investments, converting each to display currency
    let totalInvestment = 0;
    let totalRealisation = 0;
    let totalFmv = 0;
    let count = investments.length;

    investments.forEach(inv => {
      if (inv.metrics) {
        const invCurrency = inv.investment_currency || 'INR';
        totalInvestment += convertToDisplayCurrency(inv.metrics.total_investment || 0, invCurrency);
        totalRealisation += convertToDisplayCurrency(inv.metrics.total_realisation || 0, invCurrency);
        totalFmv += convertToDisplayCurrency(inv.metrics.current_stake_value || 0, invCurrency);
      }
    });

    // Calculate MOIC
    const moic = totalInvestment > 0 ? (totalRealisation + totalFmv) / totalInvestment : 0;

    setSummary({
      total_count: count,
      total_investment: totalInvestment,
      total_realisation: totalRealisation,
      total_fmv: totalFmv,
      moic: moic,
      portfolio_irr: 0
    });

    // Fetch accurate IRR from backend (only if we have investments)
    if (count > 0) {
      fetchIRR();
    }
  };

  const fetchIRR = async () => {
    try {
      const params = {};
      if (fmvDate) params.fmv_date = fmvDate;
      if (selectedFund) params.fund_id = selectedFund;
      if (selectedStage) params.stage = selectedStage;

      const response = await axios.get(`${API_URL}/investments/portfolio/summary`, { params });
      setSummary(prev => ({
        ...prev,
        portfolio_irr: response.data.portfolio_irr || 0
      }));
    } catch (error) {
      console.error('Failed to fetch portfolio IRR:', error);
    }
  };

  const handleDateInputChange = (date) => {
    // Just update the temp date, don't trigger API call yet
    setTempFmvDate(date);
  };

  const applyDateFilter = () => {
    // Apply the date filter and trigger API call
    setFmvDate(tempFmvDate);
    if (onFmvDateChange) {
      onFmvDateChange(tempFmvDate);
    }
  };

  const clearDateFilter = () => {
    setTempFmvDate('');
    setFmvDate('');
    if (onFmvDateChange) {
      onFmvDateChange('');
    }
  };

  const formatAmount = (amount, format = currencyFormat) => {
    if (!amount && amount !== 0) return '-';
    
    // Amount is already converted to display currency during summary calculation
    const displayAmount = amount;
    
    const currencySymbol = selectedCurrency === 'INR' ? '₹' : '$';
    const locale = selectedCurrency === 'INR' ? 'en-IN' : 'en-US';
    
    switch (format) {
      case 'crores':
        return `${currencySymbol}${(displayAmount / 10000000).toFixed(2)} Cr`;
      case 'millions':
        return `${currencySymbol}${(displayAmount / 1000000).toFixed(2)} M`;
      case 'full':
      default:
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: selectedCurrency,
          maximumFractionDigits: 0
        }).format(displayAmount);
    }
  };

  if (loading && !summary) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      {/* Header with Controls */}
      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <h2 className="text-xl font-bold text-gray-900">Portfolio Summary</h2>
        
        <div className="flex flex-wrap items-center gap-4">
          {/* FMV Date Selector */}
          <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-2">
            <Calendar size={18} className="text-gray-500" />
            <input
              type="date"
              value={tempFmvDate}
              onChange={(e) => handleDateInputChange(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="border-0 focus:outline-none focus:ring-0 text-sm cursor-pointer w-32"
              style={{ colorScheme: 'light' }}
            />
            <button
              onClick={applyDateFilter}
              disabled={!tempFmvDate || tempFmvDate === fmvDate}
              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Apply
            </button>
            {fmvDate && (
              <button
                onClick={clearDateFilter}
                className="text-xs text-red-600 hover:text-red-700 font-medium"
              >
                Clear
              </button>
            )}
          </div>

          {/* Currency Selector */}
          <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-2">
            <DollarSign size={18} className="text-gray-500" />
            <select
              value={selectedCurrency}
              onChange={(e) => {
                const newCurrency = e.target.value;
                setSelectedCurrency(newCurrency);
                if (onCurrencyChange) {
                  onCurrencyChange(newCurrency, exchangeRate);
                }
              }}
              className="border-0 focus:outline-none focus:ring-0 text-sm cursor-pointer"
            >
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
            </select>
          </div>

          {/* Exchange Rate Input (visible when USD is selected) */}
          {selectedCurrency === 'USD' && (
            <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-2">
              <span className="text-xs text-gray-600">Rate:</span>
              <input
                type="number"
                value={exchangeRate}
                onChange={(e) => {
                  const newRate = parseFloat(e.target.value) || 83.5;
                  setExchangeRate(newRate);
                  if (onCurrencyChange) {
                    onCurrencyChange(selectedCurrency, newRate);
                  }
                }}
                step="0.01"
                min="0"
                className="border-0 focus:outline-none focus:ring-0 text-sm w-20"
                placeholder="83.5"
              />
              <span className="text-xs text-gray-500">INR/USD</span>
            </div>
          )}

          {/* Currency Format Selector */}
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setCurrencyFormat('full')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                currencyFormat === 'full'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Full
            </button>
            <button
              onClick={() => setCurrencyFormat('crores')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                currencyFormat === 'crores'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Crores
            </button>
            <button
              onClick={() => setCurrencyFormat('millions')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                currencyFormat === 'millions'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Millions
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Total Count */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-blue-500 rounded-lg">
                <PieChart size={20} className="text-white" />
              </div>
            </div>
            <p className="text-sm text-blue-600 font-medium mb-1">Total Investments</p>
            <p className="text-2xl font-bold text-blue-900">{summary.total_count}</p>
          </div>

          {/* Total Invested */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-500 rounded-lg">
                <DollarSign size={20} className="text-white" />
              </div>
            </div>
            <p className="text-sm text-green-600 font-medium mb-1">Total Invested</p>
            <p className="text-xl font-bold text-green-900">
              {formatAmount(summary.total_investment)}
            </p>
          </div>

          {/* Total Realisation */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-purple-500 rounded-lg">
                <TrendingUp size={20} className="text-white" />
              </div>
            </div>
            <p className="text-sm text-purple-600 font-medium mb-1">Total Realisation</p>
            <p className="text-xl font-bold text-purple-900">
              {formatAmount(summary.total_realisation)}
            </p>
          </div>

          {/* Total FMV */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Activity size={20} className="text-white" />
              </div>
            </div>
            <p className="text-sm text-orange-600 font-medium mb-1">
              Total FMV {fmvDate && `(${new Date(fmvDate).toLocaleDateString()})`}
            </p>
            <p className="text-xl font-bold text-orange-900">
              {formatAmount(summary.total_fmv)}
            </p>
          </div>

          {/* MOIC */}
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-indigo-500 rounded-lg">
                <TrendingUp size={20} className="text-white" />
              </div>
            </div>
            <p className="text-sm text-indigo-600 font-medium mb-1">MOIC</p>
            <p className="text-2xl font-bold text-indigo-900">
              {summary.moic.toFixed(2)}x
            </p>
            <p className="text-xs text-indigo-600 mt-1">
              {((summary.moic - 1) * 100).toFixed(1)}% return
            </p>
          </div>

          {/* Portfolio IRR */}
          <div className={`bg-gradient-to-br rounded-lg p-4 border ${
            summary.portfolio_irr >= 0
              ? 'from-teal-50 to-teal-100 border-teal-200'
              : 'from-red-50 to-red-100 border-red-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${
                summary.portfolio_irr >= 0 ? 'bg-teal-500' : 'bg-red-500'
              }`}>
                <Activity size={20} className="text-white" />
              </div>
            </div>
            <p className={`text-sm font-medium mb-1 ${
              summary.portfolio_irr >= 0 ? 'text-teal-600' : 'text-red-600'
            }`}>
              Portfolio IRR
            </p>
            <p className={`text-2xl font-bold ${
              summary.portfolio_irr >= 0 ? 'text-teal-900' : 'text-red-900'
            }`}>
              {summary.portfolio_irr.toFixed(2)}%
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioSummary;
